// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Domain jargon, defined for a reader who knows nothing about vehicle statistics. */
export interface GlossaryEntry {
  term: string;
  title: string;
  body: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  stock: {
    term: 'stock',
    title: 'The fleet (a stock), not sales (a flow)',
    body:
      'Every figure here counts vehicles registered and on the road on 31 January 2025 — the whole fleet. That is a "stock". New-car "sales" are a "flow" that trickles into the stock each year. Because Australians keep a car about 11 years, the fleet turns over slowly, so a big change in what people are buying takes many years to show up in what is actually on the road. This is why battery-electric cars can be a large share of new sales yet a tiny share of the fleet.',
  },
  bev: {
    term: 'bev',
    title: 'Battery-electric (BEV)',
    body:
      'A vehicle driven only by an electric motor and a battery (plus the handful of hydrogen fuel-cell vehicles), with no petrol or diesel engine and no tailpipe. BITRE groups battery-electric and fuel-cell together as one motive-power class. This is what most people mean by "an EV".',
  },
  hybrid: {
    term: 'hybrid',
    title: 'Hybrid',
    body:
      'A vehicle with both a petrol engine and an electric motor — including self-charging hybrids (like a Toyota that never plugs in) and plug-in hybrids. It still burns fuel, so it is counted separately from fully electric vehicles. "Electrified" on this site means hybrid + battery-electric together.',
  },
  electrified: {
    term: 'electrified',
    title: 'Electrified share',
    body:
      'The share of vehicles that have any electric drive — battery-electric plus hybrids. It is a broader, more generous measure than the battery-electric share. Watch how much bigger it gets when you filter to recent model years.',
  },
  motive: {
    term: 'motive',
    title: 'Motive power (fuel type)',
    body:
      'How the vehicle is driven, as recorded by the state registries: Petrol, Diesel, Hybrid electric, Battery/Fuel-cell electric, Dual fuel (usually LPG) and Other. This site groups Dual fuel, Other and not-stated into a single "Other" slice.',
  },
  yom: {
    term: 'yom',
    title: 'Year of manufacture',
    body:
      'The model year the vehicle was built — not when it was bought or first registered. Filtering the fleet by year of manufacture is the cleanest way to see what was being added to the roads at that time, and how the fuel mix of new vehicles has changed.',
  },
  passenger: {
    term: 'passenger',
    title: 'Passenger vehicles',
    body:
      'Cars and car-like vehicles used mainly to carry people — sedans, hatches, wagons and most SUVs. It excludes light commercial vehicles (utes and vans), trucks, buses and motorcycles, which have very different fuel mixes and are shown separately in the Vehicle Types view. Most fuel-share figures on this site are for passenger vehicles.',
  },
  lcv: {
    term: 'lcv',
    title: 'Light commercial vehicles',
    body:
      'Utes, vans and other goods vehicles up to 3.5 tonnes. In Australia this class is overwhelmingly diesel — the HiLux/Ranger/Triton ute fleet — which is a big reason diesel keeps rising across the whole road fleet.',
  },
  poa: {
    term: 'poa',
    title: 'Postal Area (POA)',
    body:
      'The ABS approximates Australia Post postcodes with statistical areas called Postal Areas so data can be mapped against them. They are very close to postcodes but not identical, and PO-box-only postcodes have no area at all, so a few postcodes appear in the tables but not on the map.',
  },
  perturbation: {
    term: 'perturbation',
    title: 'Confidentialisation',
    body:
      'To protect privacy, BITRE randomly nudges small vehicle counts up or down by a little and suppresses cells with fewer than three vehicles. The nudges roughly cancel out, so national and state totals are reliable, but tiny cells (a rare fuel in a small postcode) can be a little off, and detailed counts will not sum exactly to the headline totals.',
  },
  registered: {
    term: 'registered',
    title: 'Registered for road use',
    body:
      'The data counts vehicles with a current registration for unrestricted use on public roads, reported by the state and territory registries through the national NEVDIS system. It excludes off-road recreational vehicles, restricted-use veteran/vintage vehicles, and defence and consular vehicles.',
  },
};

export function glossaryTerm(term: string, label?: string): string {
  const entry = GLOSSARY[term];
  const text = label ?? entry?.title ?? term;
  return `<span class="glossary-link" data-term="${term}" tabindex="0" role="button">${text}</span>`;
}

export function infoIcon(term: string): string {
  return `<button class="info-icon" data-term="${term}" aria-label="What does this mean?" type="button">i</button>`;
}
